import { ArrowRight, Zap, BatteryCharging, PlugZap } from "lucide-react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { PageHero, SectionHead, CTABand } from "@/components/Section";
import { Reveal } from "@/components/Reveal";

const PILLARS = [
  { title: "Clean Energy", text: "Generation technologies designed to eliminate dependence on carbon-intensive fuels." },
  { title: "Hybrid Energy Systems", text: "Architectures that combine carbon-free generation with renewable inputs and storage for dependable output." },
  { title: "Renewable Integration", text: "Systems engineered to work alongside solar and other renewable sources as they scale." },
  { title: "Energy Accessibility", text: "Products designed for off-grid and grid-fragile environments, from rural communities to growing towns." },
  { title: "Sustainable Technology", text: "Material, efficiency and lifecycle thinking embedded in product development." },
  { title: "Engineering Innovation", text: "A prototype-first R&D culture — build, test, validate, then publish." },
];

const PIPELINE = [
  { stage: "Concept", desc: "Problem definition, architecture and feasibility." },
  { stage: "Prototype", desc: "Working hardware — the 500W CFG prototype stage." },
  { stage: "Validation", desc: "Testing, measurement and independent verification." },
  { stage: "Pilot", desc: "Controlled real-world deployments with partners." },
  { stage: "Scale", desc: "Manufacturing readiness and market rollout." },
];

export default function Technology() {
  return (
    <>
      <Seo
        title="Technology — BNM Sustainable Energy Limited"
        description="BNM's technology approach: clean energy, hybrid generation and storage systems, renewable integration and engineering innovation for energy access in Sierra Leone and Africa."
      />
      <PageHero
        testid="technology-hero"
        eyebrow="Technology"
        lines={["Engineering clean", "power systems that", "actually deliver."]}
        lead="BNM develops hybrid carbon-free generation and storage technology — built on disciplined engineering and designed for real-world energy conditions across Africa."
      />

      <section data-testid="tech-pillars" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            eyebrow="Our Approach"
            title="Six principles behind every BNM system."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <article className="h-full rounded-lg border border-slate-200 bg-white p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                  <p className="font-mono text-xs text-emerald-600">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="tech-architecture" className="relative overflow-hidden bg-obsidian noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            dark
            eyebrow="Technology Architecture"
            title="A three-layer energy architecture."
            lead="BNM systems are organised around a simple, robust flow: generate cleanly, store intelligently, deliver reliably. Detailed engineering documentation is shared with technical partners under agreement."
          />
          <Reveal delay={0.15}>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
              {[
                { icon: Zap, layer: "Layer 01", name: "Carbon-Free Generation", desc: "The core generation unit — the focus of the Carbon Free Generator programme." },
                { icon: BatteryCharging, layer: "Layer 02", name: "Storage & Management", desc: "Integrated energy storage with control and protection systems." },
                { icon: PlugZap, layer: "Layer 03", name: "Power Delivery", desc: "Stable, usable output for homes, institutions and enterprises." },
              ].map((l) => (
                <article key={l.name} className="rounded-lg border border-white/10 bg-pine/60 p-8">
                  <l.icon className="h-7 w-7 text-volt" aria-hidden="true" />
                  <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.2em] text-volt/70">{l.layer}</p>
                  <h3 className="mt-2 font-display text-lg font-bold tracking-tight text-white">{l.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{l.desc}</p>
                </article>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
              Conceptual architecture — detailed specifications under development
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="tech-pipeline" className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            eyebrow="Innovation Pipeline"
            title="How BNM technology moves forward."
            lead="Every BNM product travels the same disciplined path. The Carbon Free Generator is currently in the prototype stage."
          />
          <ol className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {PIPELINE.map((s, i) => (
              <Reveal as="li" key={s.stage} delay={i * 0.08}>
                <div className={`h-full rounded-lg border p-6 ${i === 1 ? "border-emerald-500 bg-emerald-50" : "border-slate-200 bg-mist"}`}>
                  <p className="font-mono text-xs text-emerald-600">0{i + 1}</p>
                  <h3 className="mt-3 font-display text-base font-bold tracking-tight text-slate-900">{s.stage}</h3>
                  <p className="mt-2 text-xs leading-relaxed text-slate-600">{s.desc}</p>
                  {i === 1 && (
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-emerald-700">Current stage</p>
                  )}
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section data-testid="tech-future" className="bg-mist grid-lines-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <SectionHead
              eyebrow="Future Development"
              title="Today's prototype is tomorrow's platform."
              lead="The 500W prototype is the foundation of a product family: a 5kVA-class hybrid carbon-free generator, storage-first systems and configurable community power solutions."
            />
            <Reveal delay={0.1}>
              <Link
                to="/carbon-free-generator"
                data-testid="tech-cfg-cta"
                className="inline-flex items-center gap-2 rounded-md bg-obsidian px-7 py-3.5 font-semibold text-white transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-pine hover:text-volt active:translate-y-0"
              >
                See the Carbon Free Generator
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CTABand
        title="Interested in the engineering?"
        text="Technical and research partners can request deeper documentation through our partnerships channel."
        primary={{ label: "Start a Technical Conversation", to: "/contact", testid: "tech-contact-cta" }}
        secondary={{ label: "Innovation & R&D", to: "/innovation", testid: "tech-rnd-cta" }}
        testid="technology-cta"
      />
    </>
  );
}
